import { StatusModal } from '@/components/StatusModal';
import type {
  ProductSuggestion,
  SkladLocation,
  SkladMovement,
  SkladStock,
} from '@/components/vyroba/types';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useCallback, useEffect, useState } from 'react';

/** ERP timestamp 'YYYY-MM-DD HH:MM:SS' → 'DD.MM. HH:MM'. */
function fmtErpDateTime(s: string): string {
  const [date, time] = s.split(' ');
  const [, m, d] = (date ?? '').split('-');
  return d && m ? `${d}.${m}. ${(time ?? '').slice(0, 5)}` : s;
}

type SizeRow = { key: string; size: string | null; order: number | null };

/** Stav zápisu do Shoptetu (přebytek), vrácený backendem. */
type ShoptetStatus = { ok: boolean; error?: string };

/**
 * Naskladnění z výroby (2. krok výroby) — kusy jdou z výroby na skladovou
 * POZICI. Zapisuje se rovnou do skladového ERP (d364315_erp na kzo11) přes
 * PHP wrapper: product_stocks += qty + řádek do stock_movements. Obrazovka je
 * jako „Zadání do výroby", jen místo termínu se vybírá skladová pozice.
 */
export function NaskladneniZVyrobyPage() {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<ProductSuggestion[]>([]);
  const [picked, setPicked] = useState<ProductSuggestion | null>(null);
  const [locations, setLocations] = useState<SkladLocation[]>([]);
  const [locError, setLocError] = useState<string | null>(null);
  const [bulkPos, setBulkPos] = useState('');
  const [qtyByKey, setQtyByKey] = useState<Record<string, string>>({});
  const [posByKey, setPosByKey] = useState<Record<string, string>>({});
  const [current, setCurrent] = useState<SkladStock[]>([]);
  const [movements, setMovements] = useState<SkladMovement[]>([]);
  const [submitting, setSubmitting] = useState<string | null>(null);
  // Klíče řádků, které se právě naskladnily — krátce probliknou zeleně.
  const [doneKeys, setDoneKeys] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  // Řádky velikostí. Bez variant → jeden řádek „celý produkt" (klíč '').
  const sizeRows: SizeRow[] = picked
    ? picked.sizes.length > 0
      ? picked.sizes.map((s, i) => ({ key: s, size: s, order: i + 1 }))
      : [{ key: '', size: null, order: null }]
    : [];

  useEffect(() => {
    apiFetch<{ locations: SkladLocation[] }>('/api/vyroba/sklad/locations')
      .then((d) => setLocations(d.locations))
      .catch((e: unknown) =>
        setLocError(e instanceof ApiError ? e.describe() : 'Nepodařilo se načíst skladové pozice'),
      );
  }, []);

  const refreshMovements = useCallback(async () => {
    try {
      const d = await apiFetch<{ movements: SkladMovement[] }>(
        '/api/vyroba/sklad/movements?limit=100',
      );
      setMovements(d.movements);
    } catch {
      /* log není kritický */
    }
  }, []);

  useEffect(() => {
    refreshMovements();
  }, [refreshMovements]);

  // Našeptávač produktů s debounce (stejně jako v „Zadání do výroby").
  useEffect(() => {
    if (picked || query.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const d = await apiFetch<{ products: ProductSuggestion[] }>(
          `/api/vyroba/products?q=${encodeURIComponent(query.trim())}`,
        );
        if (!cancelled) setSuggestions(d.products);
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, picked]);

  async function refreshCurrent(code: string) {
    try {
      const d = await apiFetch<{ current: SkladStock[] }>(
        `/api/vyroba/sklad/stock?product_code=${encodeURIComponent(code)}`,
      );
      setCurrent(d.current);
    } catch {
      setCurrent([]);
    }
  }

  function pick(s: ProductSuggestion) {
    setPicked(s);
    setSuggestions([]);
    const keys = s.sizes.length > 0 ? s.sizes : [''];
    const qn: Record<string, string> = {};
    const pn: Record<string, string> = {};
    for (const k of keys) {
      qn[k] = '';
      pn[k] = ''; // pozice prázdná — bere se z hromadné, když řádek nemá vlastní
    }
    setQtyByKey(qn);
    setPosByKey(pn);
    refreshCurrent(s.product_code);
  }

  function reset() {
    setPicked(null);
    setQuery('');
    setQtyByKey({});
    setPosByKey({});
    setCurrent([]);
  }

  /** Pozice (název) → id z číselníku. Null, když název nesedí. */
  function locationIdByName(name: string): number | null {
    const n = name.trim();
    if (!n) return null;
    return locations.find((l) => l.name === n)?.id ?? null;
  }

  async function naskladnit(row: SizeRow): Promise<{
    shoptet?: ShoptetStatus;
    trello?: { ok: boolean; cards: number; error?: string };
  } | null> {
    if (!picked) return null;
    const qty = Number.parseInt(qtyByKey[row.key] ?? '', 10);
    // Efektivní pozice: vlastní u řádku, jinak hromadná.
    const posName = (posByKey[row.key] || bulkPos).trim();
    if (!Number.isInteger(qty) || qty <= 0) {
      setToast({ msg: 'Zadej kladné množství', kind: 'error' });
      return null;
    }
    const locId = locationIdByName(posName);
    if (!locId) {
      setToast({ msg: `Neznámá skladová pozice: ${posName || '(prázdné)'}`, kind: 'error' });
      return null;
    }
    const r = await apiFetch<{
      shoptet?: ShoptetStatus;
      trello?: { ok: boolean; cards: number; error?: string };
    }>('/api/vyroba/sklad/naskladnit', {
      method: 'POST',
      body: {
        product_code: picked.product_code,
        variant_name: row.size,
        stock_location_id: locId,
        qty,
      },
    });
    return { shoptet: r.shoptet, trello: r.trello };
  }

  // Zvýrazní řádek zeleně (potvrzení, že se naskladnil).
  function markDone(key: string) {
    setDoneKeys((p) => ({ ...p, [key]: true }));
  }

  // Zelené řádky svítí, dokud akce běží; po dokončení (submitting=null) zhasnou
  // najednou po 1,5 s. Díky tomu u „Naskladnit vše" zůstanou zelené do konce.
  useEffect(() => {
    if (submitting !== null || Object.keys(doneKeys).length === 0) return;
    const t = setTimeout(() => setDoneKeys({}), 1500);
    return () => clearTimeout(t);
  }, [submitting, doneKeys]);

  async function handleRow(row: SizeRow) {
    setSubmitting(row.key);
    try {
      const res = await naskladnit(row);
      if (res) {
        setQtyByKey((p) => ({ ...p, [row.key]: '' }));
        markDone(row.key);
        if (res.shoptet && !res.shoptet.ok) {
          setToast({
            msg: `Naskladněno, ale zápis do Shoptetu selhal: ${res.shoptet.error ?? 'chyba'}`,
            kind: 'error',
          });
        } else if (res.trello && !res.trello.ok) {
          setToast({
            msg: `Naskladněno, ale zápis do Trella selhal: ${res.trello.error ?? 'chyba'}`,
            kind: 'error',
          });
        } else {
          setToast({ msg: 'Naskladněno na sklad', kind: 'success' });
        }
        await Promise.all([refreshCurrent(picked?.product_code ?? ''), refreshMovements()]);
      }
    } catch (e: unknown) {
      setToast({
        msg: e instanceof ApiError ? e.describe() : 'Naskladnění selhalo',
        kind: 'error',
      });
    } finally {
      setSubmitting(null);
    }
  }

  async function handleAll() {
    const filled = sizeRows.filter((r) => {
      const n = Number.parseInt(qtyByKey[r.key] ?? '', 10);
      return Number.isInteger(n) && n > 0;
    });
    if (filled.length === 0) {
      setToast({ msg: 'Nejdřív vyplň množství aspoň u jedné velikosti', kind: 'error' });
      return;
    }
    if (filled.some((r) => !locationIdByName(posByKey[r.key] || bulkPos))) {
      setToast({ msg: 'Vyber platnou skladovou pozici (hromadně nebo u řádku)', kind: 'error' });
      return;
    }
    setSubmitting('all');
    let ok = 0;
    let shoptetFails = 0;
    let trelloFails = 0;
    const errors: string[] = [];
    for (const row of filled) {
      try {
        const res = await naskladnit(row);
        if (res) {
          ok++;
          setQtyByKey((p) => ({ ...p, [row.key]: '' }));
          markDone(row.key);
          if (res.shoptet && !res.shoptet.ok) shoptetFails++;
          if (res.trello && !res.trello.ok) trelloFails++;
        }
      } catch (e: unknown) {
        // Důvod chyby ukážeme přímo v hlášce (jinak by ho uživatel musel
        // hledat v Network tabu) — např. „Produkt X není ve skladovém ERP".
        errors.push(e instanceof ApiError ? e.describe() : 'neznámá chyba');
      }
    }
    setToast({
      msg:
        errors.length > 0
          ? `Naskladněno ${ok}, selhalo ${errors.length} — ${errors[0]}`
          : shoptetFails > 0
            ? `Naskladněno ${ok}, ale u ${shoptetFails}× selhal zápis do Shoptetu`
            : trelloFails > 0
              ? `Naskladněno ${ok}, ale u ${trelloFails}× selhal zápis do Trella`
              : `Naskladněno ${ok} velikostí`,
      kind: errors.length > 0 || shoptetFails > 0 || trelloFails > 0 ? 'error' : 'success',
    });
    await Promise.all([refreshCurrent(picked?.product_code ?? ''), refreshMovements()]);
    setSubmitting(null);
  }

  const busy = submitting !== null;
  const posInputCls =
    'w-32 rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-text transition-colors hover:border-text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30';

  return (
    <>
      {/* Sdílený číselník pozic pro našeptávače (input list=…). */}
      <datalist id="sklad-positions">
        {locations.map((l) => (
          <option key={l.id} value={l.name} />
        ))}
      </datalist>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">Naskladnění z výroby</h1>
        <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
          Vyhledej produkt a naskladni jeho velikosti na skladovou pozici. Zápis jde rovnou do
          skladového ERP — „Pohyby ve skladu" vpravo ukazují poslední změny.
        </p>
      </div>

      {locError && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {locError}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* ─── Levý sloupec: hledání + naskladnění ─────────────────────────── */}
        <div className="bg-surface rounded-xl shadow-card p-5 space-y-5">
          {/* Vyhledání produktu */}
          <div>
            <span className="block text-sm font-medium text-text mb-1.5">Produkt</span>
            {picked ? (
              <div className="flex items-center gap-3 border border-border rounded-lg px-3 py-2">
                {picked.image_url && (
                  <img
                    src={picked.image_url}
                    alt=""
                    className="w-10 h-10 object-contain rounded shrink-0"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-text truncate">{picked.name}</div>
                  <div className="text-xs text-text-muted font-mono">{picked.product_code}</div>
                </div>
                <button
                  type="button"
                  onClick={reset}
                  className="text-sm font-bold text-primary hover:underline cursor-pointer shrink-0"
                >
                  Změnit
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Hledej podle kódu nebo názvu…"
                  autoComplete="off"
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                {suggestions.length > 0 && (
                  <ul className="mt-1 border border-border rounded-lg max-h-56 overflow-y-auto divide-y divide-border-light">
                    {suggestions.map((s) => (
                      <li key={s.product_code}>
                        <button
                          type="button"
                          onClick={() => pick(s)}
                          className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-surface-hover transition-colors cursor-pointer"
                        >
                          {s.image_url && (
                            <img
                              src={s.image_url}
                              alt=""
                              className="w-8 h-8 object-contain rounded shrink-0"
                            />
                          )}
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm text-text truncate">{s.name}</span>
                            <span className="block text-xs text-text-muted font-mono">
                              {s.product_code}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          {/* Hromadná pozice + Naskladnit vše */}
          <div>
            <label htmlFor="bulk-pos" className="block text-sm font-medium text-text mb-1.5">
              Skladová pozice hromadně
            </label>
            <div className="flex gap-2">
              <input
                id="bulk-pos"
                type="text"
                list="sklad-positions"
                value={bulkPos}
                onChange={(e) => setBulkPos(e.target.value)}
                placeholder="např. 01-01"
                autoComplete="off"
                className={`${posInputCls} flex-1`}
              />
              <button
                type="button"
                onClick={handleAll}
                disabled={!picked || busy}
                className="bg-action hover:bg-action-dark disabled:opacity-50 text-white text-sm font-bold rounded-lg px-4 py-2 transition-colors cursor-pointer shrink-0"
              >
                {submitting === 'all' ? 'Naskladňuji…' : 'Naskladnit vše'}
              </button>
            </div>
          </div>

          {/* Tabulka velikostí */}
          {picked ? (
            <div className="border border-border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-bg text-left text-xs font-semibold text-text-secondary">
                    <th className="px-3 py-2 w-14">Velikost</th>
                    <th className="px-3 py-2 w-24">Množství</th>
                    <th className="px-3 py-2">Skladová pozice</th>
                    <th className="px-3 py-2" />
                    <th className="w-full p-0" />
                  </tr>
                </thead>
                <tbody>
                  {sizeRows.map((row) => {
                    // Kolik už je této velikosti na skladě – per pozice.
                    const rowStock = current
                      .filter((c) => (c.variant_name ?? '') === (row.size ?? '') && c.qty !== 0)
                      .sort((a, b) => (a.location_name ?? '').localeCompare(b.location_name ?? ''));
                    return (
                      <tr
                        key={row.key}
                        className={`border-t border-border-light transition-colors duration-700 ${
                          doneKeys[row.key] ? 'bg-success-light' : ''
                        }`}
                      >
                        <td className="px-3 py-2 font-medium text-text">
                          {row.size ?? 'Celý produkt'}
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="1"
                            value={qtyByKey[row.key] ?? ''}
                            onChange={(e) =>
                              setQtyByKey((p) => ({ ...p, [row.key]: e.target.value }))
                            }
                            className="w-20 border border-border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            list="sklad-positions"
                            value={posByKey[row.key] ?? ''}
                            onChange={(e) =>
                              setPosByKey((p) => ({ ...p, [row.key]: e.target.value }))
                            }
                            placeholder="např. 01-01"
                            autoComplete="off"
                            className={posInputCls}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => handleRow(row)}
                            disabled={busy}
                            className="bg-action hover:bg-action-dark disabled:opacity-50 text-white text-sm font-bold rounded-md px-4 py-1.5 transition-colors cursor-pointer"
                          >
                            {submitting === row.key ? 'Zapisuji…' : 'Naskladnit'}
                          </button>
                        </td>
                        <td className="w-full px-3 py-2 text-center align-middle text-sm text-text-secondary whitespace-nowrap">
                          {rowStock.map((c) => (
                            <div key={c.stock_location_id ?? c.location_name}>
                              {c.location_name} – {c.qty} ks
                            </div>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-text-muted">Vyhledej produkt, dole se vykreslí velikosti.</p>
          )}
        </div>

        {/* ─── Pravý sloupec: obrázek + pohyby ─────────────────────────────── */}
        <div className="space-y-6">
          {picked?.image_url && (
            <div className="bg-surface rounded-xl shadow-card p-5 flex justify-center">
              <img
                src={picked.image_url}
                alt={picked.name}
                className="max-h-64 object-contain rounded-lg"
              />
            </div>
          )}

          <div className="bg-surface rounded-xl shadow-card overflow-hidden">
            <div className="px-5 py-3 border-b border-border">
              <h2 className="text-sm font-bold text-text">Pohyby ve skladu</h2>
            </div>
            <div className="max-h-[32rem] overflow-y-auto overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-bg">
                  <tr className="text-left font-semibold text-text-secondary">
                    <th className="px-3 py-2">Datum</th>
                    <th className="px-3 py-2">Produkt</th>
                    <th className="px-3 py-2">Pozice</th>
                    <th className="px-3 py-2 text-right">Změna</th>
                    <th className="px-3 py-2 text-right">Stav</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-text-muted">
                        Zatím žádné pohyby.
                      </td>
                    </tr>
                  ) : (
                    movements.map((m) => (
                      <tr key={m.id} className="border-t border-border-light">
                        <td className="px-3 py-2 whitespace-nowrap text-text-secondary">
                          {fmtErpDateTime(m.created_at)}
                        </td>
                        <td className="px-3 py-2 font-mono text-text whitespace-nowrap">
                          {m.code}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-text-secondary">
                          {m.location_name ?? '—'}
                        </td>
                        <td
                          className={`px-3 py-2 text-right font-semibold ${
                            m.quantity_change < 0 ? 'text-danger' : 'text-success'
                          }`}
                        >
                          {m.quantity_change > 0 ? `+${m.quantity_change}` : m.quantity_change}
                        </td>
                        <td className="px-3 py-2 text-right text-text-secondary whitespace-nowrap">
                          {m.quantity_before} → {m.quantity_after}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <StatusModal message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />
      )}
    </>
  );
}
